import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { CharityService } from '../charity/charity.service';
import { BusinessService } from '../business/business.service';
import { UserRolesService } from './user-roles.service';
import { AlertService } from '../navigation/alert.service';
import { LoggerService } from '../logger/logger.service';
import { OrgContextStorageService, OrgKind } from './org-context-storage.service';
import { Charity } from '../../models/Charity';
import { Business } from '../../models/Business';

const CONTEXT_SWITCH_TIMEOUT_MS: number = 10000;

// Active org context keys in sessionStorage.
// Note: UserRolesService also writes a legacy 'charityId' key — that is NOT
// the active context and must not be used here.
const CHARITY_CONTEXT_KEY: string = 'currentCharityId';
const BUSINESS_CONTEXT_KEY: string = 'currentBusinessId';

/**
 * Switches the active charity/business context from a `charityId`/`businessId`
 * query param before activating /charity or /business routes. Push
 * notification URLs carry these params (e.g. /charity/charity-notifications?charityId=51)
 * so tapping an org notification lands in the right org, even straight
 * through the login returnUrl flow.
 *
 * Passthrough when no param is present or it already matches the current
 * context; fails closed (redirect to /main/home) on permission denial,
 * timeout, or load error.
 *
 * When there is no param AND no active context (cold entry: PWA relaunch,
 * direct link — sessionStorage starts empty), restores the user's last-used
 * org (per-account localStorage) if it is still permitted, or falls back to
 * the first permitted org. This ensures iOS PWA relaunches land in the correct
 * org for multi-org admins.
 */
export const orgContextGuard: CanActivateFn = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean> => {
  const charityService: CharityService = inject(CharityService);
  const businessService: BusinessService = inject(BusinessService);
  const userRolesService: UserRolesService = inject(UserRolesService);
  const alertService: AlertService = inject(AlertService);
  const navController: NavController = inject(NavController);
  const logger: LoggerService = inject(LoggerService);
  const orgContextStorage: OrgContextStorageService = inject(OrgContextStorageService);

  const isCharityRoute: boolean = state.url.startsWith('/charity');
  const isBusinessRoute: boolean = state.url.startsWith('/business');
  const requestedParam: string | null = isCharityRoute
    ? route.queryParamMap.get('charityId')
    : isBusinessRoute
      ? route.queryParamMap.get('businessId')
      : null;

  const currentContextKey: string = isCharityRoute ? CHARITY_CONTEXT_KEY : BUSINESS_CONTEXT_KEY;

  if (!requestedParam) {
    return ensureOrgContextOrRedirect(
      isCharityRoute,
      currentContextKey,
      charityService,
      businessService,
      userRolesService,
      navController,
      logger,
      state.url,
      alertService,
      orgContextStorage
    );
  }

  const requestedId: number = Number(requestedParam);
  if (!Number.isInteger(requestedId) || requestedId <= 0) {
    logger.warn('Ignoring malformed org id in query param', { url: state.url });
    return ensureOrgContextOrRedirect(
      isCharityRoute,
      currentContextKey,
      charityService,
      businessService,
      userRolesService,
      navController,
      logger,
      state.url,
      alertService,
      orgContextStorage
    );
  }

  if (sessionStorage.getItem(currentContextKey) === String(requestedId)) {
    return true;
  }

  try {
    await awaitRolesLoaded(userRolesService);

    if (isCharityRoute) {
      const permittedCharities: Charity[] = await firstValueFrom(
        userRolesService.permittedCharities$.pipe(take(1))
      );
      if (!permittedCharities.some((charity: Charity) => Number(charity.id) === requestedId)) {
        return denyAccess(alertService, navController, logger, 'charity', requestedId);
      }
    } else {
      const permittedBusinesses: Business[] = await firstValueFrom(
        userRolesService.permittedBusinesses$.pipe(take(1))
      );
      if (!permittedBusinesses.some((business: Business) => Number(business.id) === requestedId)) {
        return denyAccess(alertService, navController, logger, 'business', requestedId);
      }
    }

    await switchOrgContext(isCharityRoute, requestedId, charityService, businessService);
    return true;
  } catch (error) {
    logger.error('Org context switch failed', error, { url: state.url, requestedId });
    alertService.showAlertMessage(
      'Error',
      'Could not open that organization. Please try again.'
    );
    navController.navigateRoot(['/main/home']);
    return false;
  }
};

/**
 * Cold portal entry path: no usable org id in the URL. Pass through if a
 * context is already active or restoring; otherwise restore the user's
 * last-used org if it is still permitted, or fall back to the first permitted
 * org; redirect home if none are available.
 */
async function ensureOrgContextOrRedirect(
  isCharityRoute: boolean,
  currentContextKey: string,
  charityService: CharityService,
  businessService: BusinessService,
  userRolesService: UserRolesService,
  navController: NavController,
  logger: LoggerService,
  url: string,
  alertService: AlertService,
  orgContextStorage: OrgContextStorageService
): Promise<boolean> {
  const hasActiveContext: boolean = isCharityRoute
    ? charityService.currentCharity() !== null || !!sessionStorage.getItem(currentContextKey)
    : businessService.currentBusiness() !== null || !!sessionStorage.getItem(currentContextKey);
  if (hasActiveContext) {
    return true;
  }

  try {
    await awaitRolesLoaded(userRolesService);
    const orgKind: OrgKind = isCharityRoute ? 'charity' : 'business';
    const permitted: Array<{ id?: string | number }> = isCharityRoute
      ? await firstValueFrom(userRolesService.permittedCharities$.pipe(take(1)))
      : await firstValueFrom(userRolesService.permittedBusinesses$.pipe(take(1)));
    const fallbackId: number | null = resolveFallbackOrgId(
      permitted,
      orgContextStorage.getLastUsedOrgId(orgKind)
    );

    if (fallbackId === null) {
      logger.warn('Portal entered without context and user has no permitted orgs', { url });
      alertService.showAlertMessage(
        'Error',
        'You do not have access to any organizations. Please contact support if you believe this is an error.'
      );
      navController.navigateRoot(['/main/home']);
      return false;
    }

    logger.info('Cold portal entry without org context — selecting org', { url, fallbackId });
    await switchOrgContext(isCharityRoute, fallbackId, charityService, businessService);
    return true;
  } catch (error) {
    logger.error('Org context fallback failed', error, { url });
    navController.navigateRoot(['/main/home']);
    return false;
  }
}

/**
 * Prefer the last-used org id if it is present in the permitted list; fall
 * back to the first permitted org; return null if the list is empty.
 */
function resolveFallbackOrgId(
  orgs: Array<{ id?: string | number }>,
  lastUsedId: number | null
): number | null {
  if (lastUsedId !== null && orgs.some((o: { id?: string | number }) => Number(o.id) === lastUsedId)) {
    return lastUsedId;
  }
  return firstOrgId(orgs);
}

async function awaitRolesLoaded(userRolesService: UserRolesService): Promise<void> {
  // Roles load right after auth; bound the wait so a stuck stream can't hang navigation
  await firstValueFrom(
    userRolesService.rolesLoaded$.pipe(
      filter((loaded: boolean) => loaded),
      take(1),
      timeout(CONTEXT_SWITCH_TIMEOUT_MS)
    )
  );
}

function firstOrgId(orgs: Array<{ id?: string | number }>): number | null {
  if (!orgs?.length) {
    return null;
  }
  const id: number = Number(orgs[0].id);
  return Number.isInteger(id) && id > 0 ? id : null;
}

async function switchOrgContext(
  isCharityRoute: boolean,
  orgId: number,
  charityService: CharityService,
  businessService: BusinessService
): Promise<void> {
  if (isCharityRoute) {
    const currentCharityId: number | null = charityService.currentCharity()?.id
      ? Number(charityService.currentCharity()!.id)
      : null;
    if (currentCharityId !== orgId) {
      charityService.setCurrentCharity(String(orgId), true);
    }
    await awaitOrgEmission(charityService.charity$, orgId);
  } else {
    const currentBusinessId: number | null = businessService.currentBusiness()?.id
      ? Number(businessService.currentBusiness()!.id)
      : null;
    if (currentBusinessId !== orgId) {
      businessService.setCurrentBusiness(String(orgId), true);
    }
    await awaitOrgEmission(businessService.business$, orgId);
  }
}

async function awaitOrgEmission<T extends { id?: string | number }>(
  org$: Observable<T | null>,
  orgId: number
): Promise<void> {
  await firstValueFrom(
    org$.pipe(
      filter((org): org is T => !!org && Number(org.id) === orgId),
      take(1),
      timeout(CONTEXT_SWITCH_TIMEOUT_MS)
    )
  );
}

function denyAccess(
  alertService: AlertService,
  navController: NavController,
  logger: LoggerService,
  orgKind: 'charity' | 'business',
  requestedId: number
): boolean {
  logger.warn('User lacks permission for org in notification link', { orgKind, requestedId });
  alertService.showAlertMessage(
    'Access Denied',
    `You don't have permission to view this ${orgKind}.`
  );
  navController.navigateRoot(['/main/home']);
  return false;
}
